import {inject, Injectable, Injector, OnDestroy, runInInjectionContext} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {
    Auth,
    authState,
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    User as FirebaseAuthUser,
    UserCredential
} from '@angular/fire/auth';
import {
    collection,
    collectionData,
    doc,
    docData,
    DocumentReference,
    Firestore,
    query,
    where
} from '@angular/fire/firestore';
import {
    catchError,
    combineLatest,
    filter,
    first,
    firstValueFrom,
    from,
    lastValueFrom,
    map,
    Observable,
    of,
    ReplaySubject,
    Subscription,
    switchMap
} from 'rxjs';
import {AclUser, AppUser} from '../interfaces/app-user';
import {calculateCustomerFromHostname} from '../util';
import {environment} from '../../environments/environment';
import {ConfigService} from './config.service';

// This is the modern way to get an authorization code from Google.
// You must load the GSI script in your index.html:
// <script src="https://accounts.google.com/gsi/client" async defer></script>
declare const google: any;

interface Role {
    permissions: Set<string>;
}

@Injectable({providedIn: 'root'})
export class AuthService implements OnDestroy {
    public userStream$: Observable<AppUser | null>;
    private readonly CUSTOMER_DOMAIN;
    private _loggedUser = new ReplaySubject<AppUser | null>(1);
    private _currentUser: AppUser | null = null;

    private firebaseUser$: Observable<FirebaseAuthUser | null>;
    private userColRef;
    private userPermissions = new ReplaySubject<Set<string>>(1);
    private readonly cDR: DocumentReference;

    private auth: Auth = inject(Auth);
    private firestore: Firestore = inject(Firestore);
    private router: Router = inject(Router);
    private injector: Injector = inject(Injector);
    private http: HttpClient = inject(HttpClient);

    private userStreamSubscription: Subscription;
    private loggedUserSubscription: Subscription;

    constructor() {
        this.CUSTOMER_DOMAIN = calculateCustomerFromHostname(
            document.location.hostname
        );
        console.debug(
            `[AuthService] Initializing. Customer domain inferred from hostname: "${this.CUSTOMER_DOMAIN}"`
        );

        if (!this.CUSTOMER_DOMAIN) {
            throw new Error('Customer domain could not be determined from hostname');
        }

        this.cDR = doc(this.firestore, 'Customers', this.CUSTOMER_DOMAIN);
        this.firebaseUser$ = authState(this.auth);

        this.userColRef = collection(this.cDR, 'Users');
        const userColQuery = query(
            this.userColRef,
            where('active', '==', true),
            where('deleted', '==', false)
        );

        this.userStream$ = this.firebaseUser$.pipe(
            switchMap((firebaseUser) => {
                if (!firebaseUser) {
                    return of(null);
                }
                const uGoogleData = firebaseUser.providerData.find(
                    (pd) => pd.providerId === 'google.com'
                );
                if (!uGoogleData) {
                    return of(null);
                }
                return runInInjectionContext(this.injector, () => {
                    return (collectionData(userColQuery) as Observable<AclUser[]>).pipe(
                        switchMap((activeUsersFromDb: AclUser[]) => {
                            const dbUser = activeUsersFromDb.find(
                                (i) => i.googleUid === uGoogleData.uid
                            );
                            if (!dbUser) {
                                console.warn(
                                    `[AuthService] No active user found in the database for Google UID: ${uGoogleData.uid}`
                                );
                                return of(null);
                            }
                            const au: AppUser = {
                                ...dbUser,
                                firebaseUser: firebaseUser,
                                googleUid: uGoogleData.uid
                            };
                            this._currentUser = au;
                            return of(au);
                        })
                    );
                });
            })
        );

        this.userStreamSubscription = this.userStream$.subscribe(this._loggedUser);

        this.loggedUserSubscription = this._loggedUser
            .pipe(
                switchMap((u) => {
                    if (!u) {
                        return of(new Set([]));
                    }
                    if (!u.type) u.type = [];

                    return runInInjectionContext(this.injector, () => {
                        const oArray = u.type.map(role => {
                            const docRef = docData(doc(this.cDR, 'Roles', role)) as Observable<Role>;
                            return docRef.pipe(map((r: Role) => (r ? r.permissions : [])));
                        });
                        return combineLatest(oArray);
                    });
                }),
                map((rPerms) => {
                    const mergedPerms: any[] = [].concat(...rPerms);
                    return new Set(mergedPerms);
                })
            )
            .subscribe(this.userPermissions);
    }

    ngOnDestroy(): void {
        this.userStreamSubscription.unsubscribe();
        this.loggedUserSubscription.unsubscribe();
    }

    get currentUser(): AppUser | null {
        return this._currentUser;
    }

    get appDomain() {
        return this.CUSTOMER_DOMAIN;
    }

    public async signInWithGoogle(): Promise<void> {
        try {
            // Step 1: Sign in with Firebase to establish a session
            const provider = new GoogleAuthProvider();
            provider.addScope('https://www.googleapis.com/auth/script.projects');
            provider.addScope('https://www.googleapis.com/auth/drive');
            provider.addScope('https://www.googleapis.com/auth/script.processes');
            const userCredential = await signInWithPopup(this.auth, provider);

            // Step 2: Request an authorization code for offline access and send it to the backend.
            await this.requestAndSendAuthorizationCode(userCredential);

            this.router.navigate(['/budget']);
        } catch (error) {
            console.error('Error during Google sign-in:', error);
        }
    }

    private requestAndSendAuthorizationCode(userCredential: UserCredential): Promise<void> {
        return new Promise((resolve, reject) => {
            userCredential.user.getIdToken().then(idToken => {
                const client = google.accounts.oauth2.initCodeClient({
                    client_id: environment.clientId,
                    scope: 'https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/script.projects https://www.googleapis.com/auth/script.processes',
                    ux_mode: 'popup',
                    callback: (response: any) => {
                        if (response.code) {
                            const headers = new HttpHeaders().set('Authorization', `Bearer ${idToken}`);
                            // Step 3: Send the authorization code to your backend
                            const configService = this.injector.get(ConfigService);
                            firstValueFrom(configService.settings.pipe(
                                filter(s => s !== null && s.value?.firebaseApi !== undefined)
                            )).then((settings) => {
                                const firebaseApi = settings.value.firebaseApi;
                                return lastValueFrom(this.http.post(
                                    `${firebaseApi}/google/auth/callback`,
                                    {authorizationCode: response.code, customerDomain: this.CUSTOMER_DOMAIN},
                                    {headers}
                                ));
                            }).then(() => resolve())
                                .catch(error => {
                                    console.error('Failed to send authorization code to backend', error);
                                    reject(error);
                                });
                        } else {
                            const error = 'Google OAuth did not return an authorization code.';
                            console.error(error);
                            reject(new Error(error));
                        }
                    }
                });
                client.requestCode();
            }).catch(error => {
                console.error('Failed to get user ID token', error);
                reject(error);
            });
        });
    }

    public getUserIdToken = () => {
        return this.userStream$.pipe(
            switchMap((u) => {
                if (!u) {
                    return of(null);
                }
                return from(u.firebaseUser.getIdToken());
            }),
            catchError((err) => {
                console.warn(
                    '[AuthService] Failed to retrieve Firebase ID token for the current user.',
                    err
                );
                return of(null);
            })
        );
    };

    public logout() {
        return signOut(this.auth);
    }

    public userHasPermission = (permission: string) => {
        return this.userPermissions.pipe(
            map((permSet) => permSet.has(permission))
        );
    };

    public userHasPermissions = (permissions: string[]) => {
        return this.userPermissions.pipe(
            map((permSet) => permissions.map(p => permSet.has(p)))
        );
    };

    public isASysAdmin(user: AclUser) {
        return user && user.type && user.type.includes('sysadmin');
    }

    public getGoogleAccessToken(): Observable<{ accessToken: string } | null> {
        // This requires the config service to be available
        const configService = this.injector.get(ConfigService);

        return combineLatest([
            this.getUserIdToken().pipe(first()),
            configService.settings.pipe(filter(s => s !== null))
        ]).pipe(
            switchMap(([tid, settings]) => {
                if (!tid) {
                    console.error('getGoogleAccessToken: Firebase ID token not available.');
                    return of(null);
                }
                const endPoint = settings.value.firebaseApi + '/google/auth/accessToken';
                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${tid}`,
                    'X-App-Domain': this.appDomain
                });
                return this.http.get<{ accessToken: string }>(endPoint, {headers});
            }),
            catchError(err => {
                if (err instanceof HttpErrorResponse && err.status === 401) {
                    // Invalid refresh token. Prompt user to re-authenticate.
                    console.warn('Google refresh token is invalid. Prompting for re-authentication.');
                    // This will trigger the full sign-in flow, which gets a new refresh token.
                    this.signInWithGoogle();
                } else {
                    console.error('Failed to get Google Access Token from backend', err);
                }
                return of(null);
            })
        );
    }
}